import React, { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import { useDispatch, useSelector } from "react-redux";
import { fetchLimitedProducts } from "../../Store/ProductsSlice";
import { useNavigate } from "react-router-dom";
import { getProductImage } from "../../Utils/Cloudinary";
import Rating from "@mui/material/Rating";
import VusProduct from "../Products/VusProduct";
import "../../Styles/PopularProducts.css";
import WhatshotIcon from '@mui/icons-material/Whatshot';


import "swiper/css";
import "swiper/css/navigation";

const CATEGORY = "POPULAIRES";

/* ── Petit badge de rang (or / argent / bronze / numérique) ── */
const RankBadge = ({ rank }) => {
    const classes = {
        1: "pp-rank pp-rank-gold",
        2: "pp-rank pp-rank-silver",
        3: "pp-rank pp-rank-bronze",
    };
    return (
        <span className={classes[rank] ?? "pp-rank pp-rank-num"}>
            {rank}
        </span>
    );
};

const PopularProducts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showPopUp, setshowPopUp] = useState(false);

    const closePopUp = () => { setshowPopUp(false); setSelectedProduct(null); };
    const openPopUp = (product) => { setSelectedProduct(product); setshowPopUp(true); };

    const products = useSelector((state) => state.products.items[CATEGORY] || []);
    const status = useSelector((state) => state.products.statusByCategory[CATEGORY] || "idle");

    useEffect(() => {
        if (status === "idle") dispatch(fetchLimitedProducts({ category: CATEGORY, limit: 12 }));
    }, [status, dispatch]);

    const displayedProducts = useMemo(() => products.slice(0, 12), [products]);

    const formatPrice = (price) => {
        const v = typeof price === "string" ? parseFloat(price) : price;
        return new Intl.NumberFormat("fr-FR", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(v);
    };

    if (status === "loading" || status === "idle") return null;
    if (status === "failed" || displayedProducts.length === 0) return null;

    const CardContent = ({ product, rank, mobile = false }) => (
        <div className={`pp-card ${mobile ? "pp-card--mobile" : ""}`}>
            {/* Zone image */}
            <div className="pp-card__img-wrap" onClick={() => openPopUp(product)}>
                <img
                    src={getProductImage(product.image)}
                    alt={product.name}
                    className="pp-card__img"
                    loading="lazy"
                />
                <RankBadge rank={rank} />
                {rank <= 3 && <span className="pp-card__hot">HOT</span>}
            </div>

            {/* Zone infos */}
            <div className="pp-card__body">
                <p className="pp-card__name" title={product.name}>
                    {product.name}
                </p>
                <div className="pp-card__stars">
                    <Rating defaultValue={4.5} precision={0.5} size="small" readOnly />
                    <span className="pp-card__star-val">4.5</span>
                </div>
                <p className="pp-card__price">
                    {formatPrice(product.price)}&nbsp;
                    <span className="pp-card__currency">FCFA</span>
                </p>
                <button className="pp-card__btn" onClick={() => openPopUp(product)}>
                    + Panier
                </button>
            </div>
        </div>
    );

    return (
        <section className="pp-section">
            <div className="container-fluid">

                {/* ── En-tête ── */}
                <div className="pp-header">
                    <div className="pp-header__left">
                        <div className="pp-header__accent" />
                        <h2 className="pp-header__title">Produits populaires</h2>
                        <span className="pp-header__badge"><WhatshotIcon style={{ color: "orange" }} /> Top {displayedProducts.length}</span>
                    </div>
                    <button
                        className="pp-header__viewall"
                        onClick={() => navigate("/products?popular=true")}
                    >
                        Voir tout
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>
                <div className="pp-divider" />

                {/* ── Desktop : Swiper ── */}
                <Swiper
                    modules={[Navigation, Autoplay]}
                    navigation
                    autoplay={{ delay: 3500, disableOnInteraction: false }}
                    spaceBetween={14}
                    breakpoints={{
                        768: { slidesPerView: 4 },
                        992: { slidesPerView: 6 },
                    }}
                    className="pp-swiper d-none d-md-block"
                >
                    {displayedProducts.map((product, index) => (
                        <SwiperSlide key={product.id}>
                            <CardContent product={product} rank={index + 1} />
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* ── Mobile : scroll horizontal natif ── */}
                <div className="pp-mobile-track d-md-none">
                    {displayedProducts.map((product, index) => (
                        <CardContent key={product.id} product={product} rank={index + 1} mobile />
                    ))}
                </div>

            </div>

            {showPopUp && (
                <VusProduct closePopUp={closePopUp} product={selectedProduct} />
            )}
        </section>
    );
};

export default React.memo(PopularProducts);
